import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import React, { useEffect, useRef, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { Navigate } from 'react-router-dom'
import { loginRequest } from '../../../config/msalConfig'
import { useMicrosoftSession } from '../../../helpers/useMicrosoftSession'

/**
 * Where MSAL sends the browser back after a redirect sign-in, which is what the
 * login page falls back to when a popup is blocked.
 *
 * MSAL processes the response as the app boots, but a Microsoft account is not an
 * app session: the token still has to be exchanged. Nothing did that on this path,
 * so a user whose popup was blocked signed in with Microsoft and landed back on the
 * login page logged out. This finishes the job and sends them to the dashboard.
 */
const MsalRedirect = () => {
  const { instance, accounts, inProgress } = useMsal()
  const { completeMicrosoftLogin } = useMicrosoftSession()
  const [done, setDone] = useState<'pending' | 'signedIn' | 'failed'>('pending')
  // The effect can run twice for one redirect; the exchange must not.
  const exchanging = useRef(false)

  useEffect(() => {
    if (inProgress !== InteractionStatus.None || exchanging.current) return

    const account = instance.getActiveAccount() ?? accounts[0]
    if (!account) {
      setDone('failed')
      return
    }

    exchanging.current = true

    const exchange = async () => {
      try {
        const result = await instance.acquireTokenSilent({ ...loginRequest, account })
        const signedIn = result?.accessToken
          ? await completeMicrosoftLogin(result.accessToken)
          : false
        setDone(signedIn ? 'signedIn' : 'failed')
      } catch (error) {
        // A silent acquire can legitimately need the user again; the login page is
        // where they can do that, so fall back to it rather than looping here.
        if (!(error instanceof InteractionRequiredAuthError)) {
          console.error('Microsoft redirect sign-in failed:', error)
        }
        setDone('failed')
      }
    }

    exchange()
  }, [instance, accounts, inProgress, completeMicrosoftLogin])

  if (done === 'signedIn') return <Navigate to='/' replace />
  if (done === 'failed') return <Navigate to='/login' replace />

  return (
    <div className='d-flex justify-content-center align-items-center py-5'>
      <Spinner animation='border' role='status' className='me-2' />
      <span>Signing you in...</span>
    </div>
  )
}

export default MsalRedirect
